import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { getDiffLines } from '../../utils/getDiffLines'
import { isValidJson } from '../../utils/jsonUtils'

import { Flex, Spacer, Typography } from '@percent/lemonade'
import styles from './DiffViewer.module.scss'

interface DiffViewerProps {
  originalJson: string
  editedJson: string
}

interface DiffOp {
  type: 'equal' | 'add' | 'del'
  line: string
  id?: string
}

type CommonLineProps = Readonly<{ line: string }>

function InvalidJsonError({
  isOriginalValid,
  isEditedValid,
  t
}: Readonly<{
  isOriginalValid: boolean
  isEditedValid: boolean
  t: (key: string) => string
}>) {
  return (
    <Flex direction="column" gap="xs" className={styles.errorContainer}>
      <Typography style={{ fontWeight: 'bold', color: '#cbd5e1' }}>{t('stateMachine.diff.invalidJson')}</Typography>
      <Flex direction="column" gap="xs" className={styles.errorContent}>
        {!isOriginalValid && (
          <Typography style={{ color: '#cbd5e1' }}>• {t('stateMachine.diff.originalInvalid')}</Typography>
        )}
        {!isEditedValid && (
          <Typography style={{ color: '#cbd5e1' }}>• {t('stateMachine.diff.editedInvalid')}</Typography>
        )}
        <Typography style={{ color: '#cbd5e1' }}>{t('stateMachine.diff.fixJsonFirst')}</Typography>
      </Flex>
    </Flex>
  )
}

function StateHeader({ line }: Readonly<CommonLineProps>) {
  return <Flex className={styles.stateHeader}>{line}</Flex>
}

function Separator({ line }: Readonly<CommonLineProps>) {
  return (
    <Flex data-testid="diff-separator" className={styles.separator}>
      {line}
    </Flex>
  )
}

function NoChanges({ line }: Readonly<CommonLineProps>) {
  return <div className={styles.noChanges}>✓ {line}</div>
}

function AddedLine({ line }: Readonly<CommonLineProps>) {
  return (
    <Flex className={styles.addLine} align="center">
      <span className={`${styles.symbol} ${styles.addSymbol}`}>+</span>
      <span className={styles.content}>{line}</span>
    </Flex>
  )
}

function DeletedLine({ line }: Readonly<CommonLineProps>) {
  return (
    <Flex className={styles.deleteLine} align="center">
      <span className={`${styles.symbol} ${styles.deleteSymbol}`}>-</span>
      <span>{line}</span>
    </Flex>
  )
}

function UnchangedLine({ line }: Readonly<CommonLineProps>) {
  return (
    <Flex className={styles.equalLine} align="center">
      <span className={`${styles.symbol} ${styles.equalSymbol}`} />
      <span className={styles.content}>{line}</span>
    </Flex>
  )
}

function DiffLine({ op }: Readonly<{ op: DiffOp }>) {
  if (op.line === '') {
    return <Spacer size={8} axis="vertical" />
  }

  let LineComponent: React.ComponentType<CommonLineProps>

  switch (true) {
    case op.type === 'equal' && op.line.startsWith('State:'):
      LineComponent = StateHeader
      break
    case op.type === 'equal' && op.line.includes('─'):
      LineComponent = Separator
      break
    case op.type === 'equal' && op.line === 'No changes detected':
      LineComponent = NoChanges
      break
    case op.type === 'equal':
      LineComponent = UnchangedLine
      break
    case op.type === 'add':
      LineComponent = AddedLine
      break
    case op.type === 'del':
      LineComponent = DeletedLine
      break
    default:
      return null
  }

  return <LineComponent line={op.line} />
}

export function DiffViewer({ originalJson, editedJson }: Readonly<DiffViewerProps>) {
  const { t } = useTranslation()

  const isOriginalValid = useMemo(() => isValidJson(originalJson), [originalJson])
  const isEditedValid = useMemo(() => isValidJson(editedJson), [editedJson])

  const diffOps = useMemo(() => {
    if (!isOriginalValid || !isEditedValid) {
      return []
    }

    const lines = getDiffLines(originalJson, editedJson)

    return lines.map((op, index) => ({
      ...op,
      id: `${op.type}-${index}-${op.line.substring(0, 20)}`
    }))
  }, [originalJson, editedJson, isOriginalValid, isEditedValid])

  if (!isOriginalValid || !isEditedValid) {
    return <InvalidJsonError isOriginalValid={isOriginalValid} isEditedValid={isEditedValid} t={t} />
  }

  return (
    <div className={styles.diffContainer}>
      {diffOps.map(op => (
        <DiffLine key={op.id} op={op} />
      ))}
    </div>
  )
}
